import { useMemo } from "react";

import { Application, StackStatus } from "src/domains/application/types";
import type { ManagedResourceUI } from "src/domains/resource/types";
import {
  computeHealthCounts,
  HealthCounts,
} from "src/shell/components/HealthSummaryChips";

export interface ProjectStats {
  totalSystems: number;
  runningSystems: number;
  lastDeployed: Date | null;
  lastDeployedLabel: string;
  healthCounts: HealthCounts;
  /** Combined health counts across resources + applications. */
  combinedHealthCounts: HealthCounts;
  totalResources: number;
  runningResources: number;
  healthPct: number;
  healthyCount: number;
}

/**
 * Hook for aggregating project statistics from systems and resources.
 */
export function useProjectStats(
  systems: Application[],
  resources: ManagedResourceUI[] = [],
): ProjectStats {
  return useMemo(() => {
    const totalSystems = systems.length;
    const runningSystems = systems.filter(
      (s) => s.status === StackStatus.RUNNING,
    ).length;

    // Calculate last deployed time
    const deployedTimestamps = systems
      .filter((s) => s.deployedAt)
      .map((s) => new Date(s.deployedAt!).getTime())
      .filter((t) => !isNaN(t));

    const lastDeployedMs =
      deployedTimestamps.length > 0 ? Math.max(...deployedTimestamps) : 0;

    const lastDeployed = lastDeployedMs > 0 ? new Date(lastDeployedMs) : null;

    const lastDeployedLabel = lastDeployed
      ? lastDeployed.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

    const healthCounts = computeHealthCounts(systems.map((s) => s.status));
    const resourceHealthCounts = computeHealthCounts(
      resources.map((r) => r.status),
    );

    const combinedHealthCounts: HealthCounts = {
      running: healthCounts.running + resourceHealthCounts.running,
      stopped: healthCounts.stopped + resourceHealthCounts.stopped,
      failed: healthCounts.failed + resourceHealthCounts.failed,
      other: healthCounts.other + resourceHealthCounts.other,
    };

    const totalResources = resources.length;
    const runningResources = resourceHealthCounts.running;
    const totalCombined =
      combinedHealthCounts.running +
      combinedHealthCounts.stopped +
      combinedHealthCounts.failed +
      combinedHealthCounts.other;
    const healthyCount = combinedHealthCounts.running;
    const healthPct =
      totalCombined > 0 ? Math.round((healthyCount / totalCombined) * 100) : 0;

    return {
      totalSystems,
      runningSystems,
      lastDeployed,
      lastDeployedLabel,
      healthCounts,
      combinedHealthCounts,
      totalResources,
      runningResources,
      healthPct,
      healthyCount,
    };
  }, [systems, resources]);
}

export default useProjectStats;
